// Pure helpers — no React, no JSX.

export const CATEGORIES = ["protocol", "tool", "incident", "research", "adoption", "vendor"];

const RU_MONTHS_SHORT = [
  "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
  "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
];
const EN_MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Parse a YYYY-MM-DD or YYYY-MM or YYYY into {y,m,d} (1-indexed m,d). */
export function parseDate(s) {
  const parts = String(s).split("-");
  return {
    y: parseInt(parts[0], 10),
    m: parts[1] ? parseInt(parts[1], 10) : 1,
    d: parts[2] ? parseInt(parts[2], 10) : 1,
  };
}

function quarterOf(month) {
  return Math.floor((month - 1) / 3) + 1;
}

/** Render a date string for the event header, respecting precision + language. */
export function formatDate(dateStr, precision, lang) {
  const { y, m, d } = parseDate(dateStr);
  if (precision === "year") {
    return String(y);
  }
  if (precision === "quarter") {
    return `Q${quarterOf(m)} ${y}`;
  }
  if (precision === "month") {
    const months = lang === "ru" ? RU_MONTHS_SHORT : EN_MONTHS_SHORT;
    return `${months[m - 1]} ${y}`;
  }
  // day
  const months = lang === "ru" ? RU_MONTHS_SHORT : EN_MONTHS_SHORT;
  if (lang === "ru") {
    return `${d} ${months[m - 1].toLowerCase()} ${y}`;
  }
  return `${months[m - 1]} ${String(d).padStart(2, "0")}, ${y}`;
}

export const PRECISION_LABEL = {
  day:     { en: "exact",   ru: "точно" },
  month:   { en: "month",   ru: "месяц" },
  quarter: { en: "quarter", ru: "квартал" },
  year:    { en: "year",    ru: "год" },
};

/** Sort comparator: ascending by parsed date. */
export function compareDate(a, b) {
  const da = parseDate(a.date), db = parseDate(b.date);
  return (da.y - db.y) || (da.m - db.m) || (da.d - db.d);
}

/** Returns all unique tags across events, sorted by frequency desc, then alpha. */
export function collectTags(events) {
  const counts = new Map();
  for (const e of events) for (const t of (e.tags || [])) {
    counts.set(t, (counts.get(t) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag, count]) => ({ tag, count }));
}

/** Pure filter — easy to test. */
export function applyFilters(events, { categories, tags, query, lang }) {
  const q = query.trim().toLowerCase();
  return events.filter((e) => {
    if (categories.size && !categories.has(e.category)) return false;
    if (tags.size) {
      for (const t of tags) if (!(e.tags || []).includes(t)) return false;
    }
    if (q) {
      const hay = [
        e.title?.en, e.title?.ru, e.summary?.en, e.summary?.ru,
        e.impact?.en, e.impact?.ru,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** Group filtered events by year. Newest year first; within a year, newest first. */
export function groupByYear(events) {
  const sorted = [...events].sort((a, b) => -compareDate(a, b));
  const groups = new Map();
  for (const e of sorted) {
    const y = parseDate(e.date).y;
    if (!groups.has(y)) groups.set(y, []);
    groups.get(y).push(e);
  }
  return [...groups.entries()].map(([year, items]) => ({ year, items }));
}

export const CATEGORY_LABEL = {
  protocol: { en: "protocol", ru: "протокол" },
  tool:     { en: "tool",     ru: "инструмент" },
  incident: { en: "incident", ru: "инцидент" },
  research: { en: "research", ru: "исследование" },
  adoption: { en: "adoption", ru: "распространение" },
  vendor:   { en: "vendor",   ru: "вендор" },
};

export const UI_STRINGS = {
  en: {
    title: "DevOps × AI Agents Timeline",
    subtitle: "How the DevOps role evolved as LLM agents arrived (2010–2026).",
    lastUpdated: "Last updated",
    search: "Search title, summary, impact…",
    tagPlaceholder: "Filter by tag",
    all: "All",
    showing: "Showing",
    of: "of",
    events: "events",
    clearAll: "Clear all",
    cleared: "Filters cleared",
    expand: "Impact & sources",
    collapse: "Hide impact",
    emptyTitle: "No matching events",
    emptyBody: "Try removing a filter or clearing your search.",
    emptyAction: "Clear filters",
    contribute: "Contribute on GitHub",
    builtWith: "Built with Claude",
    confidenceLow:    "Lower confidence — single-source or vendor PR",
    confidenceMedium: "Medium confidence — limited corroboration",
    confidenceHigh:   "High confidence — multiple reputable sources",
    confidence: "confidence",
    impact: "Impact",
    sources: "Sources",
    tag: "tag",
    cat: "cat",
    text: "search",
    light: "Light",
    dark:  "Dark",
  },
  ru: {
    title: "DevOps × AI Agents Timeline",
    subtitle: "Как менялась роль DevOps с приходом LLM-агентов (2010–2026).",
    lastUpdated: "Обновлено",
    search: "Поиск по названию, описанию, влиянию…",
    tagPlaceholder: "Фильтр по тегу",
    all: "Все",
    showing: "Показано",
    of: "из",
    events: "событий",
    clearAll: "Сбросить",
    cleared: "Фильтры сброшены",
    expand: "Влияние и источники",
    collapse: "Скрыть",
    emptyTitle: "Событий не найдено",
    emptyBody: "Попробуйте снять фильтр или очистить поиск.",
    emptyAction: "Сбросить фильтры",
    contribute: "Внести правки на GitHub",
    builtWith: "Сделано в Claude",
    confidenceLow:    "Низкая уверенность — единственный источник или вендорский PR",
    confidenceMedium: "Средняя уверенность — мало подтверждений",
    confidenceHigh:   "Высокая уверенность — несколько независимых источников",
    confidence: "уверенность",
    impact: "Влияние",
    sources: "Источники",
    tag: "тег",
    cat: "кат",
    text: "поиск",
    light: "Светлая",
    dark:  "Тёмная",
  },
};
